<script>
import { mapActions, mapGetters } from 'vuex';
import dayjs from 'dayjs';
import { getContextId, debounce } from '@/util/utils';
import { LOADING_KEY, UNIT_TYPE_PACK } from '@/util/constants';

import SinglePage from '@/views/admin/SinglePage.vue';
import EzEntityInfo from '@/components/ui/EntityInfo';
import EzLoadMore from '@/components/ui/LoadMore';
import EzSpinner from '@/components/ui/Spinner';
import EzButton from '@/components/ui/Button';
import EzTable from '@/components/ui/Table';
import VAccountOwnerEntityInfo from '@/components/v3/patterns/VAccountOwnerEntityInfo';
import VDataWithInfo from '@/components/v3/elements/VDataWithInfo';
import EzFilterList from '@/components/ui/FilterList';
import VFilterDropdown from '@/components/v3/patterns/VFilterDropdown';
import EzSelect from '@/components/ui/Select/EzSelect.vue';
import VSelectSearch from '@/components/v3/patterns/VSelectSearch';
import EzProductGroupFilter from '@/components/ui/Filter/ProductGroup.vue';
import EzCategoryFilter from '@/components/ui/Filter/Category.vue';
import VDistributorEntityInfo from '@/components/v3/patterns/VDistributorEntityInfo';
import VTag from '@/components/v3/elements/VTag';
import VPrice from '@/components/v3/elements/VPrice';
import TotalIcon from '@/views/common/orders/total-icon';
import downloadAttachment from '@/util/downloadAttachment';
import EzLoader from '@/components/ui/Loader/EzLoader.vue';
import VDatePicker from '@/components/v3/patterns/VDatePicker';
import EzInput from '@/components/ui/Input/EzInput';
import EzMaskInputSimple from '@/components/ui/MaskInputSimple/EzMaskInputSimple.vue';
import EzAlert from '@/components/ui/Alert';

const FilterArea = {
  List: 'list',
  Dropdown: 'dropdown',
};

/**
 * Stocktake
 * @version 1.0.0
 * @since 3.18.0
 */

export default {
  name: 'Stocktake',
  props: {
    stocktakeId: {
      type: Number,
      required: true,
    },
  },
  components: {
    VDataWithInfo,
    SinglePage,
    EzEntityInfo,
    EzLoadMore,
    EzSpinner,
    EzButton,
    EzTable,
    VAccountOwnerEntityInfo,
    VFilterDropdown,
    EzFilterList,
    EzSelect,
    VSelectSearch,
    EzCategoryFilter,
    EzProductGroupFilter,
    VDistributorEntityInfo,
    VTag,
    VPrice,
    TotalIcon,
    EzLoader,
    VDatePicker,
    EzInput,
    EzMaskInputSimple,
    EzAlert,
  },
  data() {
    return {
      stocktake: {},
      items: [],
      meta: { page: 0 },
      dayjs,
      packUnitType: UNIT_TYPE_PACK,
      dropdownFilters: {},
      listFilters: {},
      inventoryLocations: [],
      productGroups: [],
      distributors: [],
      categories: [],
      FilterArea,
      totalAmount: null,
      totalChange: null,
      totalCogs: null,
      isEditMode: false,
      stocktakeDate: null,
      formKey: 'stocktake-edit',
      editableValues: {},
      searchTerm: '',
    };
  },
  computed: {
    ...mapGetters('loading', ['getLoading', 'isSomeLoading']),
    minDate() {
      if (this.stocktake.minStocktakeDate) {
        // Handle case where minStocktakeDate might be a timestamp number
        return this.dayjs(this.stocktake.minStocktakeDate).format('YYYY-MM-DD');
      }
      return this.dayjs().format('YYYY-MM-DD');
    },
    isAllLocationsSelected() {
      return !this.dropdownFilters.locationId || this.dropdownFilters.locationId === 'all';
    },
    editableColumnKey() {
      return this.isAllLocationsSelected ? 'totalStockValue' : 'current';
    },

    isLoading() {
      return this.isSomeLoading([LOADING_KEY.VENUE_FETCH_STOCKTAKES]);
    },
    isLoadingMore() {
      return this.getLoading(LOADING_KEY.VENUE_FETCH_MORE_STOCKTAKES);
    },
    isLoadingExport() {
      return this.getLoading(LOADING_KEY.DISTRIBUTOR_EXPORT_STOCKTAKE);
    },
    venueId() {
      return getContextId();
    },
    productGroupsFilter() {
      return [{ id: null, name: 'All Product Groups' }, ...this.productGroups];
    },
    distributorFilter() {
      return this.distributors;
    },
    inventoryLocationsFilter() {
      return [{ id: null, name: 'All Locations' }, ...this.inventoryLocations];
    },
    note() {
      if (this.dropdownFilters.locationId) {
        return (
          this.stocktake.locationStocktakes.filter(
            item => item.location.id === this.dropdownFilters.locationId,
          )[0]?.note || null
        );
      }
      return this.stocktake.note;
    },
    users() {
      if (this.dropdownFilters.locationId) {
        return (
          this.stocktake.locationStocktakes.filter(
            item => item.location.id === this.dropdownFilters.locationId,
          )[0]?.users || []
        );
      }
      return this.stocktake.users;
    },
    isCompleted() {
      return this.stocktake.status === 'completed';
    },
    canEditStocktake() {
      return this.$permission.has('editStocktake') && this.stocktake.isEditable;
    },
    stocktimeLabel() {
      if (!this.stocktake.stocktakeTime) return '';

      return this.stocktake.stocktakeTime === 'end_of_day'
        ? 'End of'
        : 'Beginning of';
    },
  },
  methods: {
    ...mapActions('entities/venues', [
      'venueFetchStocktake',
      'venueFetchStocktakeItems',
      'fetchVenueLocations',
      'venueFetchDistributorFilterOptions',
      'venueFetchStocktakeTotalAmount',
      'venueExportSingleStocktake',
      'venueUpdateProductQuantity',
      'venueUpdateStocktakeItemTotal',
      'venueUpdateStocktakeFinish'
    ]),
    ...mapActions('entities/products', ['venueFetchProductGroups']),
    ...mapActions('entities/categories', ['venueFetchCategoriesNew']),

    onSearchInput: debounce(async function onSearchInputHandler(value) {
      this.searchTerm = value;
      this.meta = { page: 0 };
      await this.fetchItems();
    }, 300),

    updateStocktakeValue: debounce(async function updateStocktakeValueHandler(product, value) {
      const newValue = parseFloat(value) || 0;

      const currentValue = this.isAllLocationsSelected
        ? this.getEditableValue(product, 'totalValue')
        : this.getEditableValue(product, 'actualInventory');
      if (currentValue === newValue) return;

      try {
        if (this.isAllLocationsSelected) {
          await this.venueUpdateStocktakeItemTotal({
            venueId: this.venueId,
            stocktakeId: this.stocktakeId,
            itemId: product.id,
            totalValue: newValue,
            loadingKey: LOADING_KEY.VENUE_UPDATE_STOCKTAKE_ITEM_TOTAL,
          });

          this.$set(product, 'totalValue', newValue);
        } else {
          const payload = {
            venueId: this.venueId,
            stocktakeId: this.stocktakeId,
            locationId: this.dropdownFilters.locationId,
            body: {
              productId: product.id,
              quantity: newValue,
            },
            loadingKey: LOADING_KEY.VENUE_UPDATE_STOCKTAKE_QUANTITY,
          };

          await this.venueUpdateProductQuantity(payload);

          this.$set(product, 'actualInventory', newValue);
        }

      } catch (error) {
        // handle error
      }
    }, 500),

    getEditableValue(product, columnName) {
      const columnMap = {
        totalValue: product.totalValue || 0,
        actualInventory: product.actualInventory || 0,
      };

      return columnMap[columnName] || 0;
    },

    async finishEditing() {
      try {
        await this.venueUpdateStocktakeFinish({
          venueId: this.venueId,
          stocktakeId: this.stocktakeId,
          stocktakeDate: this.stocktakeDate,
          loadingKey: LOADING_KEY.VENUE_UPDATE_STOCKTAKE_FINISH,
        });

        this.isEditMode = false;
        const loadedPages = this.meta.page;
        this.meta = { page: 0 };
        await this.fetchStocktake();

        await this.fetchItems();
        if (loadedPages >= 2) {
          await this.fetchItems(LOADING_KEY.VENUE_FETCH_MORE_STOCKTAKES);
        }
      } catch (error) {
        // handle error
      }
    },
    enterEditMode() {
      this.isEditMode = true;
      this.stocktakeDate = this.dayjs(this.stocktake.completedAt).format('YYYY-MM-DD');
    },

    updateStocktakeDate(newDate) {
      this.stocktakeDate = newDate.format('YYYY-MM-DD');
    },
    async fetchStocktake() {
      const { data } = await this.venueFetchStocktake({
        venueId: this.venueId,
        stocktakeId: this.stocktakeId,
      });
      this.stocktake = data.data;
    },
    async fetchItems(loadingKey = LOADING_KEY.VENUE_FETCH_STOCKTAKES) {
      const query = {
        page: this.meta.page + 1,
        ...this.dropdownFilters,
      };

      if (this.searchTerm && this.searchTerm.trim()) {
        query.term = this.searchTerm.trim();
      }

      const { data } = await this.venueFetchStocktakeItems({
        stocktakeId: this.stocktakeId,
        venueId: this.venueId,
        loadingKey,
        query,
      });

      if (loadingKey === LOADING_KEY.VENUE_FETCH_STOCKTAKES) {
        this.items = data.data;
        const { data: { data: totals } } = await this.venueFetchStocktakeTotalAmount({
          stocktakeId: this.stocktakeId,
          venueId: this.venueId,
          query: this.dropdownFilters,
        });
        this.totalAmount = totals.value;
        this.totalChange = totals.change;
        this.totalCogs = totals.cogs;
      } else {
        this.items = [...this.items, ...data.data];
      }
      this.meta = data.meta;
    },

    async exportStocktake() {
      const { data, headers } = await this.venueExportSingleStocktake({
        stocktakeId: this.stocktakeId,
        venueId: this.venueId,
      });
      const filename = (headers['content-disposition'] || '').match(/filename="(.+)"/);
      const name = Array.isArray(filename) ? decodeURI(filename[1]) : 'template.xlsx';
      downloadAttachment(data, name);
    },
    async loadMore() {
      await this.fetchItems(LOADING_KEY.VENUE_FETCH_MORE_STOCKTAKES);
    },
    async fetchLocations() {
      const {
        data: { data },
      } = await this.fetchVenueLocations({ venueId: this.venueId });
      this.inventoryLocations = data;
    },
    async fetchProductGroups() {
      const {
        data: { data },
      } = await this.venueFetchProductGroups();
      this.productGroups = data;
    },
    async fetchDistributors() {
      const { data } = await this.venueFetchDistributorFilterOptions({
        venueId: this.venueId,
        query: { for: 'products' },
      });
      this.distributors = data.data;
    },
    async fetchCategoriesFilter() {
      const {
        data: { data },
      } = await this.venueFetchCategoriesNew();
      this.categories = data;
    },
    updateFilters: debounce(async function deb(area, event) {
      // Handle productGroupId from List area - update dropdownFilters to keep both synced
      if (area === FilterArea.List && event[0] === 'productGroupId') {
        const value = event[1];
        this.dropdownFilters = {
          ...this.dropdownFilters,
          productGroupId: value?.id || null
        };
      } else if (area === FilterArea.List && event[0] !== 'locationId' && event[0] !== 'productGroupId') {
        const [filterName, value] = event;
        this.listFilters = { ...this.listFilters, [filterName]: value };
      } else if (area === FilterArea.Dropdown || event[0] === 'locationId') {
        this.dropdownFilters = {
          ...this.dropdownFilters,
          ...(typeof event.categoryId === 'object' &&
            event.categoryId !== null && { categoryId: event.categoryId.id }),
          ...(typeof event.distributorId === 'object' &&
            event.distributorId !== null && { distributorId: event.distributorId.id }),
          ...(typeof event.productGroupId === 'object' &&
            event.productGroupId !== null && { productGroupId: event.productGroupId.id }),
          ...(typeof event.locationId === 'object' &&
            event.locationId !== null && { locationId: event.locationId.id }),
          ...(event[0] === 'locationId' && { locationId: event[1].id }),
        };
        if (event.distributorId === undefined) delete this.dropdownFilters.distributorId;
        this.$nextTick(() => this.$refs.filtersGroup.closeFilterDropdown());
      }
      this.meta = {
        page: 0,
      };
      await this.fetchItems();
    }, 300),
    async resetFilters(area) {
      if (area === FilterArea.List) {
        Object.keys(this.listFilters).forEach(key => {
          this.listFilters[key] = null;
        });
        this.searchTerm = '';
      } else if (area === FilterArea.Dropdown) {
        this.dropdownFilters = {};
      }
      this.$nextTick(() => this.$refs.filtersGroup.closeFilterDropdown());
      this.meta = { page: 0 };
      await this.fetchItems();
    },
    getInitials(user) {
      return (user || '')
        .match(/(\b\S)?/g)
        .join('')
        .toUpperCase();
    },
    positions(row) {
      return row.locations.filter(item => item.location.id === this.dropdownFilters.locationId)[0]
        ?.positions;
    },
    getLocation(row) {
      if (row.locations.length === 1) return row.locations[0].location.name;
      return 'Multiple';
    },
    tooltip(row) {
      const locations = row.locations.map(
        l => `
        <div class="inventory-item">
          <strong class="inventory-item__label">
            ${l.location.name}:
          </strong>
          <span class="inventory-item__number">
            ${l.actualInventory}
          </span>
        </div>`,
      );

      const total = `
        <div class="inventory-item inventory-item--total">
          <strong class="inventory-item__label">
            Total:
          </strong>
          <span class="inventory-item__number">
            ${row.actualInventory}
          </span>
        </div>`;

      const tooltipContent = `
        <div class="v-product-quantity-inventory__tooltip">
          ${locations.join('')}
          ${total}
        </div>`;

      return {
        html: true,
        placement: 'end',
        classes: ['tooltip--reset-margin', 'm-8'],
        content: tooltipContent,
      };
    },
  },
  async created() {
    await Promise.all([
      this.fetchStocktake(),
      this.fetchItems(),
      this.fetchLocations(),
      this.fetchProductGroups(),
      this.fetchCategoriesFilter(),
      this.fetchDistributors(),
    ]);
  },
};
</script>

<template>
  <div>
    <single-page>
      <!-- Edit Mode Banner -->
      <ez-alert
        v-if="isEditMode"
        type="yellow-gray"
        size="inline"
        class="edit-mode-banner mb-24"
        :style="{ justifyContent: 'center' }"
      >
        <template #icon>
          <font-awesome-icon icon="info-circle" />
        </template>
        Edit mode active. To update current count, please filter by location. Total Stock Value will be recalculated once you click Finish.
      </ez-alert>

      <template #breadcrumbs>
        <template>
          <router-link :to="{ name: 'venue-stock-stocktake' }">
            <font-awesome-icon icon="arrow-left" /> Back to Counts
          </router-link>
        </template>
      </template>
      <template #title>
        <div v-if="!isEditMode">
          <h1 class="header__title">Count</h1>
          <p class="header__createdAt">
            <span v-if="stocktimeLabel" class="stocktime-label">{{ stocktimeLabel }}</span>
            <strong>{{ stocktake.completedAt | date }}</strong>
          </p>
        </div>
        <div v-else class="edit-mode-header">
          <h1 class="header__title">Edit Count</h1>
          <div class="edit-date-container">
            <v-date-picker
              class="date-picker mt-16"
              name="stocktakeDate"
              label="Stocktake Date"
              v-model="stocktakeDate"
              :formKey="formKey"
              :min="minDate"
              @dateChange="updateStocktakeDate"
            />
          </div>
        </div>
      </template>
      <template #actions>
        <ez-button
          v-if="!isEditMode && canEditStocktake"
          type="secondary"
          @click="enterEditMode"
        >
          Edit
        </ez-button>
        <ez-button
          v-if="isEditMode"
          type="primary"
          @click="finishEditing"
        >
          Finish
        </ez-button>
        <ez-button
          v-if="!isEditMode && isCompleted"
          type="secondary"
          :isLoading="isLoadingExport"
          @click="exportStocktake"
        >
          Export CSV
        </ez-button>
      </template>

      <div class="subheader">
        <div class="subheader__section">
          <span class="subheader__label">Note</span>
          <p class="subheader__text">
            {{ note || '-' }}
          </p>
        </div>
        <div class="subheader__section">
          <span class="subheader__label">Users</span>
          <div class="subheader__users">
            <v-account-owner-entity-info
              v-for="(user, idx) in users"
              :key="idx"
              class="subheader__users__user"
              :account-owner="{
                initials: getInitials(user),
                name: user,
                id: idx,
                colors: ['#6C7995', '#ECEEF5'],
              }"
              :to-show-name="false"
            />
          </div>
        </div>
        <div class="products-header__valuation-wrapper">
          <div v-if="totalCogs" class="products-header__valuation">
            <div class="products-header__valuation__amount-wrapper">
            <span class="products-header__valuation__amount">
              {{ totalCogs | price }}
            </span>
              <small>Cost of Goods Sold</small>
            </div>
          </div>
          <div class="products-header__valuation">
            <div class="products-header__valuation__amount-wrapper">
            <span class="products-header__valuation__amount">
              {{ totalChange | price }}
            </span>
              <small>Change in Inventory</small>
            </div>
          </div>
          <div class="products-header__valuation">
            <div class="products-header__valuation__amount-wrapper">
            <span class="products-header__valuation__amount">
              {{ totalAmount | price }}
            </span>
              <small>Total Inventory</small>
            </div>
            <span class="products-header__valuation__icon">
            <span class="products-header__valuation__icon__inner">
              <total-icon />
            </span>
          </span>
          </div>
        </div>
      </div>

      <div class="stocktake__actions mb-24" :class="{ 'edit-mode': isEditMode }">
        <div class="search-input-container mr-16">
          <ez-input
            formKey="product-search"
            name="search"
            placeholder="Search for an Item"
            :value="searchTerm"
            @onChange="onSearchInput"
          >
            <template #prefix>
              <font-awesome-icon icon="search" />
            </template>
          </ez-input>
        </div>

        <ez-filter-list
          :filters="listFilters"
          @filterUpdated="(...$event) => updateFilters(FilterArea.List, $event)"
          @resetFilter="resetFilters(FilterArea.List)"
          class="mr-16"
          :disabled="false"
        >
          <ez-select
            class="input-group locations-filter"
            name="locationId"
            :value="dropdownFilters.locationId"
            :selected="dropdownFilters.locationId"
            :options="inventoryLocationsFilter"
            :disabled="false"
          />
          <ez-product-group-filter
            name="productGroupId"
            label="Product Group"
            :productGroups="productGroupsFilter"
            :selected="dropdownFilters.productGroupId"
            :disabled="false"
          />
        </ez-filter-list>

        <v-filter-dropdown
          :filters="dropdownFilters"
          @filterUpdated="$event => updateFilters(FilterArea.Dropdown, $event)"
          @resetFilters="resetFilters(FilterArea.Dropdown)"
          ref="filtersGroup"
          :disabled="false"
        >
          <template #firstRow>
            <ez-product-group-filter
              name="productGroupId"
              label="Product Group"
              :productGroups="productGroupsFilter"
              :selected="dropdownFilters.productGroupId"
              isFullWidth
              :disabled="false"
            />
            <v-select-search
              class="select-supplier"
              :selected="dropdownFilters.distributorId"
              formKey="filters"
              name="distributorId"
              :label="`${$t('global.distributor')}`"
              :data="distributorFilter"
              :placeholder="`All ${$t('global.distributors')}`"
              :searchPlaceholder="`Select ${$t('global.distributor')}`"
              align-left
              is-full-width
              :disabled="false"
            >
              <template #result="{ result }">
                <v-distributor-entity-info :distributor="result" />
              </template>
            </v-select-search>
            <ez-category-filter
              name="categoryId"
              label="Category"
              :categories="categories"
              :selected="dropdownFilters.categoryId"
              isFullWidth
              :disabled="false"
            />
            <ez-select
              class="input-group product-group-filter"
              name="locationId"
              :value="dropdownFilters.locationId"
              :selected="dropdownFilters.locationId"
              label="Location"
              :options="inventoryLocationsFilter"
              isFullWidth
              :disabled="false"
            />
          </template>
        </v-filter-dropdown>
      </div>
      <ez-table
        :data="items"
        :columns="[
          'name',
          'unit',
          'countingOrder',
          'priorInventory',
          ...(!dropdownFilters.locationId ? ['addedInventory'] : []),
          'actualInventory',
          'inventoryChange',
          ...(!dropdownFilters.locationId ? ['valueChange'] : []),
          ...(!dropdownFilters.locationId ? ['totalCogs'] : []),
          'value',
          'totalValue',
        ]"
        :headers="{
          name: () => 'Product',
          countingOrder: () => `${dropdownFilters.locationId ? 'Counting Order' : 'Location'}`,
          priorInventory: () => 'Prior',
          value: () => 'Per Unit',
          addedInventory: () => 'Additions',
          actualInventory: () => 'Current',
          inventoryChange: () => 'Change(#)',
          valueChange: () => 'Change($)',
          totalCogs: () => 'COGS($)',
          totalValue: () => 'Total',
        }"
        :columnProps="{
          name: { class: 'name-cell' },
          countingOrder: { class: 'medium-cell counting-cell' },
          priorInventory: { class: 'small-cell' },
          actualInventory: { class: 'small-cell overflow-cell' },
          addedInventory: { class: 'additions-cell' },
          inventoryChange: { class: 'medium-cell difference-cell' },
          note: { class: 'large-cell note-cell' },
          unit: { class: 'medium-cell unit-cell' },
          value: { class: 'value-cell price-cell' },
          valueChange: { class: 'value-cell price-cell' },
          totalCogs: { class: 'value-cell price-cell' },
          totalValue: { class: 'price-cell' },
        }"
        disableHover
      >
        <template #cell-name="{ row }">
          <ez-entity-info :imgUrl="row.image">
            <div class="product-info" :title="row.name">
              <span class="product-info__name">{{ row.name }}</span>
              <span class="product-info__sku">{{ row.sku ? row.sku : '-' }}</span>
            </div>
          </ez-entity-info>
        </template>
        <template #cell-unit="{ row }">
          <template v-if="row.orderingUnit">
            <v-data-with-info
              :info="
                row.orderingUnit.type === packUnitType
                  ? 'The quantity of pack unit products is being managed per single unit.'
                  : ''
              "
              :show-underline="row.orderingUnit.type === packUnitType"
            >{{ row.orderingUnit.label }}</v-data-with-info
            >
          </template>
        </template>
        <template #cell-countingOrder="{ row }">
          <div v-if="dropdownFilters.locationId">
            <div>
              <v-tag
                color="#983BBD"
                backgroundColor="#EEDFF4"
                class="ml-4"
                v-for="pos in positions(row)"
                :key="pos"
              >{{ pos }}</v-tag
              >
            </div>
          </div>
          <span v-else>{{ getLocation(row) }}</span>
        </template>
        <template #cell-inventoryChange="{ row: { inventoryChange } }">
          <span
            :class="[
              'bold-text',
              'difference',
              { 'difference--green': inventoryChange > 0 },
              { 'difference--red': inventoryChange < 0 },
            ]"
          >{{ inventoryChange > 0 ? '+' : '' }}{{ inventoryChange }}</span
          >
        </template>
        <template #cell-actualInventory="{ row }">
          <div v-if="!isEditMode || isAllLocationsSelected">
            <span
              v-if="!dropdownFilters.locationId"
              v-tooltip="tooltip(row)"
              :class="[
                'v-product-quantity-inventory',
                {
                  'dashed-underline': true,
                },
              ]"
            >
              {{ row.actualInventory }}
            </span>
            <span v-else>{{ row.actualInventory }}</span>
          </div>

          <div v-else class="editable-cell">
            <ez-mask-input-simple
              :name="`actualInventory-${row.id}`"
              :minValue="0"
              :value="getEditableValue(row, 'actualInventory')"
              @input="updateStocktakeValue(row, $event)"
              :precision="2"
              :has-currency="false"
            />
          </div>
        </template>

        <template #cell-value="{ row }">
          <div class="value-cell-content">
            <v-price :price="row.value || 0" />
            <font-awesome-icon
              v-if="row.isManual"
              icon="info-circle"
              class="manual-edit-icon"
              v-tooltip="{
                content: 'Manually edited',
                placement: 'top'
              }"
            />
          </div>
        </template>
        <template #cell-valueChange="{ row: { valueChange } }">
          <v-price :price="valueChange || 0" />
        </template>
        <template #cell-totalCogs="{ row: { totalCogs } }">
          <v-price :price="totalCogs || 0" />
        </template>
        <template #cell-totalValue="{ row }">
          <div v-if="!isEditMode || !isAllLocationsSelected" class="cell-value">
            <v-price :price="row.totalValue || 0" />
          </div>

          <div v-else class="editable-cell">
            <ez-mask-input-simple
              :name="`totalValue-${row.id}`"
              :minValue="0"
              :value="getEditableValue(row, 'totalValue')"
              @input="updateStocktakeValue(row, $event)"
              :precision="2"
              :has-currency="true"
            />
          </div>
        </template>

      </ez-table>

      <div v-if="isLoadingMore" class="u-text-center mt-12">
        <ez-spinner />
      </div>

      <ez-load-more v-if="!isLoadingMore && meta.lastPage > meta.page" @loadMore="loadMore" />
    </single-page>
    <ez-loader :show="isLoading">Loading...</ez-loader>
  </div>
</template>

<style lang="scss" scoped>
.header {
  margin-bottom: 12px;

  &__title {
    @include font-size(24px, 32px);
    font-weight: 500;
    margin: 0;
  }

  &__createdAt {
    margin: 4px 0 0;
    color: $color-gray-6C;

    .stocktime-label {
      margin-right: 4px;
      font-weight: 400;
    }

    strong {
      font-weight: 600;
    }
  }
}

.subheader {
  display: flex;
  align-items: flex-start;
  margin-bottom: 24px;

  &__section {
    &:nth-child(2) {
      margin-left: 40px;
    }
  }

  &__total {
    display: flex;
    align-items: center;
    margin-left: auto;

    &__amount {
      @include font-size(20px, 28px);
      color: $color-gray-25;
      font-weight: 600;
      margin-right: 12px;
    }

    &__icon {
      @extend %flex-center;
      justify-content: center;
      color: $color-white;
      background-color: $color-pastel-orange;
      width: 2.5rem;
      height: 2.5rem;
      border-radius: 50%;

      &__inner {
        @include font-size(11px);
        @extend %flex-center;
        justify-content: center;
        background-color: $color-primary-orange;
        border-radius: 50%;
        width: 1.875rem;
        height: 1.875rem;
      }
    }
  }

  &__label {
    @include font-size(12px, 18px);
    color: $color-gray-6C;
    font-weight: 600;
    margin-bottom: 4px;
  }

  &__text {
    margin: 0;
    width: 400px;
  }

  &__users {
    display: flex;
    align-items: center;

    &__user {
      border: 2px solid white;
      border-radius: 50%;

      &:not(:first-child) {
        margin-left: -8px;
      }
    }
  }
}


.stocktake__actions {
  display: flex;
  align-items: flex-end;

  &.edit-mode {
    .ez-filter-list {
      opacity: 1;
      pointer-events: auto;
    }

    .v-filter-dropdown {
      opacity: 1;
      pointer-events: auto;
    }

    .search-input-container {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .search-input-container.always-enabled {
    opacity: 1;
    pointer-events: auto;
  }

  .edit-mode-header {
    .header__title {
      @include font-size(24px, 32px);
      font-weight: 500;
      margin: 0 0 16px 0;
    }

    .edit-date-container {
      .date-picker {
        max-width: 200px;

        :deep() {
          .date-picker__input {
            background-color: $color-white;
            border: 1px solid #ccd2e0;

            &:focus {
              border-color: $color-primary-blue;
              box-shadow: 0 0 0 2px rgba(77, 124, 254, 0.1);
            }
          }

          .date-picker__calendar {
            z-index: 1000;
          }
        }
      }
    }
  }

  .select-supplier {
    :deep() .select-search__trigger {
      width: 100%;
      color: $color-gray-6C;
    }
  }
  :deep() .ez-select__dropdown {
    width: max-content;
  }
  :deep() .ez-select__display-container {
    height: 36px;
    svg {
      color: $color-gray-6C;
    }
  }

  .locations-filter {
    width: auto;
    :deep() .ez-select__display {
      padding-right: 12px;
    }
  }

  :deep() .dropdown-filters {
    .input-group {
      margin: 0;
    }

    .filters-area__row:not(:first-child) {
      justify-content: start;
    }
  }

  :deep() .ez-filter-list {
    &__items {
      margin: 0;
    }
  }

  .button-dropdown {
    margin-left: auto;
  }
}

.editable-cell {
  display: flex;
  justify-content: flex-end;

  :deep(.mask-input-wrapper) {
    width: 100%;
    display: flex;
    justify-content: flex-end;

    .mask-input {
      width: 80px;

      &__prefix {
        color: $color-gray-6C;
        font-size: 14px;
        font-weight: 500;
        left: 2px;
        top: 2px;
        z-index: 1;
      }

      &__input {
        color: $color-primary-blue;
        text-align: right;
        font-weight: bold;
        font-size: 1rem;
        line-height: 22px;
        padding-left: 16px;
      }
    }
  }
}

.cell-value {
  padding: 8px 0;
  line-height: 20px;
}

.product-info {
  display: flex;
  flex-direction: column;

  &__name {
    color: $color-gray-25;
    font-weight: 600;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__sku {
    @include font-size(12px, 14px);
    color: $color-gray-6C;
    font-weight: 500;
  }
}
.unit-cell {
  overflow: hidden;
}
.additions-cell {
  width: 90px;
}
.difference-cell {
  .bold-text {
    font-weight: 600;
    color: $color-gray-25;
  }

  .difference {
    font-size: 1rem;
    line-height: 22px;
    font-weight: bold;
    &--green {
      color: $color-primary-green;
    }

    &--red {
      color: $color-primary-red;
    }
  }
}

:deep() .table {
  tbody tr td.note-cell {
    overflow: visible;

    .dashed-underline {
      @include font-size(14px);
      font-weight: 400;
      border-bottom: 2px dotted rgba($color-gray-6C, 0.64);
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  tbody tr td.overflow-cell {
    overflow: visible;
  }

  th.price-cell.value-cell {
    text-align: left;
  }

  td.price-cell.value-cell {
    color: $color-gray-6C;
    font-weight: normal;
    text-align: left;

    .v-price--column {
      align-items: flex-start;
    }

    .value-cell-content {
      display: flex;
      align-items: center;
      gap: 8px;

      .manual-edit-icon {
        color: #4D7CFE;
        font-size: 14px;
        cursor: pointer;
      }
    }
  }

  th.price-cell:not(.value-cell) {
    text-align: right;
  }

  td.price-cell:not(.value-cell) {
    text-align: right;

    .v-price--column {
      align-items: flex-end;
    }

    .cell-value {
      text-align: right;
    }
  }
}

.v-product-quantity-inventory {
  &.dashed-underline {
    border-bottom: 2px dotted rgba($color-gray-6C, 0.64);
    @include font-size(14px);

    &--out-of-stock {
      border-color: rgba($color-primary-red, 0.64);
    }
  }

  &__tooltip {
    @include font-size(12px);

    .inventory-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      min-width: 144px;
      margin-bottom: 6px;

      &--total {
        padding-top: 6px;
        border-top: 1px solid rgba(#fff, 0.2);
        margin-bottom: 0;
      }

      &__number {
        margin-left: 10px;
      }
    }
  }
}
.products-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  &__main {
    h1 {
      @include font-size(24px);
      margin: 0;
      font-weight: 500;
    }
    > span {
      @include font-size(12px);
      color: $color-gray-6C;
    }
  }
  &__valuation-wrapper {
    display: flex;
    margin-left: auto;
  }

  &__valuation {
    display: flex;
    align-items: center;

    &:not(:first-child) {
      padding-left: 16px;
      border-left: 1px solid #eceef5;
    }

    &:not(:last-child) {
      padding-right: 16px;
    }

    &__amount-wrapper {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: flex-end;
      margin-right: 12px;

      small {
        font-size: 12px;
        font-weight: 500;
        letter-spacing: 0.25px;
        color: #6c7995;
      }
    }

    &__amount {
      @include font-size(20px, 28px);
      color: $color-gray-25;
      font-weight: 600;
    }

    &__icon {
      @extend %flex-center;
      justify-content: center;
      color: $color-white;
      background-color: $color-pastel-green;
      width: 40px;
      height: 40px;
      border-radius: 50%;

      &__inner {
        @include font-size(11px);
        @extend %flex-center;
        justify-content: center;
        background-color: $color-primary-green;
        border-radius: 50%;
        width: 30px;
        height: 30px;
      }
    }
  }
}

@media (max-width: 1440px) {
  .name-cell {
    width: 300px;
  }
}

@media (max-width: 1200px) {
  .unit-cell {
    display: none;
  }
}

@media (max-width: 1024px) {
  .counting-cell {
    display: none;
  }
  .additions-cell {
    display: none;
  }
}

.edit-mode-banner {
  margin: 0;
  width: 100%;

  :deep(.alert.alert--inline) {
    justify-content: center !important;
  }
}
</style>
